import {
    ActivityLogModelType,
    ActivtyLog,
    isActivityLogChange,
} from '../../models/activty-log.model';

/** Names for ids that audit rows store, looked up from data the app already loads. */
export interface AuditRefs {
    triggerNames: Map<string, string>;
    channelNames: Map<string, string>;
    /** Audience filter operator id → display name, for rows logged before labels. */
    operatorNames: Map<string, string>;
}

export const EMPTY_AUDIT_REFS: AuditRefs = {
    triggerNames: new Map(),
    channelNames: new Map(),
    operatorNames: new Map(),
};

const UNKNOWN_TRIGGER = 'unknown trigger';

const TIME_FRAME_LABELS: Record<string, string> = {
    daily_limit: 'daily',
    weekly_limit: 'weekly',
};

const triggerName = (id: unknown, refs: AuditRefs) =>
    refs.triggerNames.get(String(id)) ?? UNKNOWN_TRIGGER;

const channelName = (key: unknown, refs: AuditRefs) =>
    refs.channelNames.get(String(key)) ?? String(key);

const snapshotOf = (log: ActivtyLog): Record<string, unknown> | undefined =>
    isActivityLogChange(log.data)
        ? log.data.after ?? log.data.before ?? undefined
        : undefined;

/**
 * What a frequency limit row applies to, e.g. "Channel: Push · daily". Their
 * snapshots only hold a channel key or trigger uuid, which mean nothing to a reader.
 */
export const getEntryScope = (
    log: ActivtyLog,
    refs: AuditRefs,
): string | undefined => {
    const snapshot = snapshotOf(log);
    if (!snapshot) return undefined;

    if (log.object_type === ActivityLogModelType.DEFAULT_FREQUENCY_LIMITS) {
        return snapshot.compound_trigger_id
            ? `Trigger: ${triggerName(snapshot.compound_trigger_id, refs)}`
            : undefined;
    }
    if (log.object_type !== ActivityLogModelType.FREQUENCY_LIMIT) {
        return undefined;
    }

    const value = snapshot.aggregation_value;
    const scope =
        snapshot.aggregation_type === 'trigger'
            ? `Trigger: ${triggerName(value, refs)}`
            : snapshot.aggregation_type === 'channel'
            ? `Channel: ${channelName(value, refs)}`
            : snapshot.aggregation_type === 'global'
            ? 'Global'
            : undefined;
    if (!scope) return undefined;

    const timeFrame = TIME_FRAME_LABELS[String(snapshot.time_frame)];
    return timeFrame ? `${scope} · ${timeFrame}` : scope;
};

/** Swaps stored ids for names in diff values; `row` is the snapshot the value came from. */
export const resolveAuditValue = (
    objectType: ActivtyLog['object_type'],
    field: string,
    value: unknown,
    row: Record<string, unknown> | null | undefined,
    refs: AuditRefs,
): unknown => {
    if (value == null) return value;
    // The backend stores display labels next to some ids (exit_event_id →
    // exit_event_name / exit_event_code); show those instead of the id.
    if (field.endsWith('_id') && row) {
        const base = field.slice(0, -3);
        for (const label of [`${base}_name`, `${base}_code`]) {
            const text = row[label];
            if (text !== null && text !== undefined && text !== '') {
                return text;
            }
        }
    }
    if (field === 'compound_trigger_id') return triggerName(value, refs);
    if (
        objectType === ActivityLogModelType.FREQUENCY_LIMIT &&
        field === 'aggregation_value'
    ) {
        if (row?.aggregation_type === 'trigger')
            return triggerName(value, refs);
        if (row?.aggregation_type === 'channel')
            return channelName(value, refs);
    }
    if (
        objectType === ActivityLogModelType.FREQUENCY_LIMIT &&
        field === 'time_frame'
    ) {
        return TIME_FRAME_LABELS[String(value)] ?? value;
    }
    return value;
};

const HIDDEN_SNAPSHOT_FIELDS = new Set(['id', 'customer_id']);

const LABEL_SUFFIX = /_(name|code)$/;

/**
 * Internal ids add nothing to a snapshot a person reads, and label fields
 * (X_name / X_code next to X_id) are already shown in place of their id.
 */
export const visibleSnapshotFields = (
    fields: string[],
    row?: Record<string, unknown> | null,
): string[] =>
    fields.filter((field) => {
        if (HIDDEN_SNAPSHOT_FIELDS.has(field)) return false;
        const labelOf = field.replace(LABEL_SUFFIX, '');
        return !(
            labelOf !== field &&
            (fields.includes(`${labelOf}_id`) ||
                (row != null && `${labelOf}_id` in row))
        );
    });

const TRIGGER_OPERATOR_LABELS: Record<string, string> = {
    equals: 'equals',
    not_equals: 'does not equal',
    greater_than: 'more than',
    less_than: 'less than',
    greater_than_equals: 'at least',
    less_than_equals: 'at most',
};

type Item = Record<string, unknown>;

const has = (v: unknown) => v !== null && v !== undefined && v !== '';

// event_name is the display name, event_code what integrations send.
const eventLabel = (item: Item, idKey: string) =>
    has(item.event_name)
        ? String(item.event_name)
        : has(item.event_code)
        ? String(item.event_code)
        : `event #${item[idKey]}`;

const ID_KEY = /(^id$|_id$)/;

const genericItem = (item: Item) =>
    Object.keys(item)
        .filter((key) => !ID_KEY.test(key) && has(item[key]))
        .map((key) => `${key}: ${String(item[key])}`)
        .join(', ');

const ITEM_FORMATTERS: Partial<
    Record<string, (item: Item, refs: AuditRefs) => string>
> = {
    'Audience.filters': (item, refs) => {
        const attribute = has(item.attribute_name)
            ? String(item.attribute_name)
            : `attribute #${item.attribute_id}`;
        const operator = has(item.operator_name)
            ? String(item.operator_name)
            : refs.operatorNames.get(String(item.operator_id)) ??
              `operator #${item.operator_id}`;
        return `${attribute} ${operator} ${String(item.value ?? '')}`.trim();
    },
    'CompoundTrigger.triggers': (item) =>
        `${eventLabel(item, 'contact_event_id')}${
            item.is_exit_event ? ' (exit event)' : ''
        }`,
    'CompoundTrigger.sub_triggers': (item) => {
        const days = Number(item.lookback);
        return `${eventLabel(item, 'contact_event_id')} ${
            TRIGGER_OPERATOR_LABELS[String(item.operator)] ?? item.operator
        } ${item.value} in the last ${days} ${days === 1 ? 'day' : 'days'}`;
    },
    'Formula.labels': (item) =>
        `${has(item.label_name) ? item.label_name : `label #${item.label_id}`}${
            has(item.variant_type) ? ` (${item.variant_type})` : ''
        }`,
    'Formula.tags': (item) =>
        has(item.tag_name) ? String(item.tag_name) : `tag #${item.tag_id}`,
    'Formula.audiences': (item) =>
        has(item.audience_name)
            ? String(item.audience_name)
            : `audience #${item.audience_id}`,
    'RewardFunction.target_events': (item) =>
        `${eventLabel(item, 'event_id')}${
            has(item.learning_quality) ? ` (${item.learning_quality})` : ''
        }`,
    'RewardFunction.weight_overrides': (item) =>
        `${eventLabel(item, 'event_id')}: ${String(item.custom_value)}`,
};

/** Keys an item must have (any of) before its specialised formatter applies. */
const ITEM_SHAPES: Record<string, string[]> = {
    'Audience.filters': ['attribute_id', 'attribute_name'],
    'CompoundTrigger.triggers': ['contact_event_id', 'event_name'],
    'CompoundTrigger.sub_triggers': ['contact_event_id', 'event_name'],
    'Formula.labels': ['label_id', 'label_name'],
    'Formula.tags': ['tag_id', 'tag_name'],
    'Formula.audiences': ['audience_id', 'audience_name'],
    'RewardFunction.target_events': ['event_id', 'event_name'],
    'RewardFunction.weight_overrides': ['event_id', 'event_name'],
};

/**
 * A list of child rows (audience filters, trigger rules, reward events) as one
 * readable line each, or undefined when the value isn't a list of objects.
 */
export const formatListValue = (
    objectType: ActivtyLog['object_type'],
    field: string,
    value: unknown,
    refs: AuditRefs,
): string[] | undefined => {
    if (!Array.isArray(value)) return undefined;
    if (!value.every((item) => item !== null && typeof item === 'object')) {
        return undefined;
    }
    const key = `${objectType}.${field}`;
    const format = ITEM_FORMATTERS[key];
    const shape = ITEM_SHAPES[key] ?? [];
    return (value as Item[]).map((item) =>
        format && shape.some((k) => k in item)
            ? format(item, refs)
            : genericItem(item),
    );
};
